import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ShoppingCart, 
  CreditCard, 
  Package, 
  AlertCircle,
  CheckCircle,
  Loader2,
  Pill,
  User,
  Building,
  MapPin,
  Calendar,
  Phone,
  Mail,
  AlertTriangle
} from 'lucide-react';
import StorefrontPaymentModal from '@/components/storefront/StorefrontPaymentModal';
import MedicationCard from '@/components/storefront/MedicationCard';
import CartSummary from '@/components/storefront/CartSummary';
import { useClinic } from '@/contexts/ClinicContext';
import PaymentAccountConfigAlert from '@/components/clinic/PaymentAccountConfigAlert';
import { formatDOB } from '@/lib/dateUtils';

const StorefrontPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [selectedMedications, setSelectedMedications] = useState<string[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [cartTotal, setCartTotal] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  // TODO (Phase 2): populate these from the fetched program data once this page
  // is wired to a real API call — set via setProgramServiceFee / setClinicFacilitationFee
  // after loading program_price and clinic_facilitation_fee from the response.
  const [programServiceFee, setProgramServiceFee] = useState<number | undefined>(undefined);
  const [clinicFacilitationFee, setClinicFacilitationFee] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [prescription] = useState([]);
  const { clinic,isAdmin } = useClinic();

  // Add CSS for background pattern
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .bg-grid-pattern {
        background-image: 
          linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px);
        background-size: 20px 20px;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);




  // Handle medication selection
  const handleMedicationToggle = (medicationId: string, selected: boolean) => {
    if (selected) {
      setSelectedMedications([...selectedMedications, medicationId]);
    } else {
      setSelectedMedications(selectedMedications.filter(id => id !== medicationId));
    }
  };

  // Handle payment success
  const handlePaymentSuccess = (transactionData: any) => {
    setShowPaymentModal(false);
    navigate(`/storefront/confirmation/${transactionData.transactionId}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600" />
          <p className="mt-4 text-gray-600">Loading prescription details...</p>
        </div>
      </div>
    );
  }

  // Check if token is valid (dummy validation)
  if (!token || token === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-red-500" />
              <h2 className="mt-4 text-xl font-semibold">Invalid or Expired Link</h2>
              <p className="mt-2 text-gray-600">
                This prescription link is no longer valid. Please contact your healthcare provider for assistance.
              </p>
              <Button 
                className="mt-4"
                onClick={() => navigate('/')}
              >
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Modern Header Section */}
        <motion.div
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-blue-100/50 shadow-sm mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

          <div className="relative px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
              {/* Left Section - Title and Description */}
              <motion.div
                className="flex-1"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <motion.div
                    className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg"
                    whileHover={{ scale: 1.05, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Package className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      NovaMed Pharmacy
                    </h1>
                    <p className="text-gray-600 mt-1 text-lg">
                      Secure Online Checkout
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Right Section - Badges */}
              <motion.div
                className="flex items-center gap-2"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Badge variant="outline" className="text-sm">
                  <CreditCard className="h-3 w-3 mr-1" />
                  Secure Checkout
                </Badge>
                <Badge variant="outline" className="text-sm">
                  Token: {token?.slice(0, 8)}...
                </Badge>
              </motion.div>
            </div>
          </div>
        </motion.div>
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
              <span className="ml-2 text-sm font-medium">Review</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
              <span className="ml-2 text-sm text-gray-500">Payment</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
              <span className="ml-2 text-sm text-gray-500">Confirmation</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Patient & Clinic Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-600" />
                    Prescription Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Patient</p>
                        <p className="font-medium">{prescription.patient.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Date of Birth</p>
                        <p className="font-medium flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDOB(prescription.patient.dob)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Gender</p>
                        <p className="font-medium">{prescription.patient.gender}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Prescriber</p>
                        <p className="font-medium">{prescription.practitioner.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Clinic</p>
                        <p className="font-medium flex items-center gap-1">
                          <Building className="h-3 w-3" />
                          {prescription.clinic.name}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Contact</p>
                        <p className="text-sm flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {prescription.clinic.phone}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                      <MapPin className="h-3 w-3 inline mr-1" />
                      Shipping Address
                    </p>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm font-medium">
                        {prescription.shipping_address.line_1}
                        {prescription.shipping_address.line_2 && 
                          <>, {prescription.shipping_address.line_2}</>
                        }
                      </p>
                      <p className="text-sm text-gray-600">
                        {prescription.shipping_address.city}, {prescription.shipping_address.stateInfo.code} {prescription.shipping_address.postal_code}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Medications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Pill className="h-5 w-5 text-blue-600" />
                    Select Medications
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose which medications you'd like to purchase. Uncheck any medications you don't need.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {prescription.medications.map((medication, index) => (
                      <MedicationCard
                        key={medication.id}
                        medication={medication}
                        isSelected={selectedMedications.includes(medication.id)}
                        onToggle={(selected) => 
                          handleMedicationToggle(medication.id, selected)
                        }
                        index={index}
                      />
                    ))}
                  </div>
                  
                  {selectedMedications.length === 0 && (
                    <Alert className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Please select at least one medication to proceed with checkout.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Cart Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <CartSummary
                selectedMedications={prescription.medications.filter(m =>
                  selectedMedications.includes(m.id)
                )}
                cartTotal={cartTotal}
                taxAmount={taxAmount}
                finalTotal={finalTotal}
                onProceedToPayment={() => setShowPaymentModal(true)}
                disabled={selectedMedications.length === 0}
                programServiceFee={programServiceFee}
                clinicFacilitationFee={clinicFacilitationFee}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <StorefrontPaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        token={token!}
        amount={finalTotal}
        onSuccess={handlePaymentSuccess}
        shippingAddress={prescription.shipping_address}
        programServiceFee={programServiceFee}
        clinicFacilitationFee={clinicFacilitationFee}
      />
    </div>
  );
};

export default StorefrontPage; 